import React, { useState, useEffect } from "react";
import { Typography, Box, useTheme, CircularProgress } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { useGetMatrixDataQuery } from "store/slices/apis/VendorDashboardSlice";
import { useParams } from "react-router-dom";

const ratingToScore = {
  R1: 1,
  R2: 2,
  R3: 3,
  R4: 4,
  R5: 5,
};

const scoreToColor = [
  { from: 1, to: 1, color: "#4CAF50" },
  { from: 2, to: 2, color: "#8BC34A" },
  { from: 3, to: 3, color: "#FFC107" },
  { from: 4, to: 4, color: "#FF9800" },
  { from: 5, to: 5, color: "#F44336" },
];

const VendorHeatmap = () => {
  const theme = useTheme();
  const [series, setSeries] = useState([]);
  const [xAxisCategories, setXAxisCategories] = useState([]);
  const [yAxisVendors, setYAxisVendors] = useState([]);

  const { projectId } = useParams();
  const { data, isLoading, error } = useGetMatrixDataQuery({ projectId });
  const rawData = data || [];

  useEffect(() => {
    const vendorSet = new Set();
    const catList = [];
    const tempMatrix = {};

    rawData.forEach((entry) => {
      const category = entry.category;
      catList.push(category);

      entry.ratings.forEach((r) => {
        vendorSet.add(r.vendor);
        if (!tempMatrix[r.vendor]) tempMatrix[r.vendor] = {};
        tempMatrix[r.vendor][category] = ratingToScore[r.rating];
      });
    });

    const vendorList = Array.from(vendorSet);
    setXAxisCategories(catList);
    setYAxisVendors(vendorList);

    const transformedData = vendorList.map((vendor) => ({
      name: vendor,
      data: catList.map((cat) => ({
        x: cat,
        y: tempMatrix[vendor][cat] || 0,
        customValues: tempMatrix[vendor][cat] || 0,
      })),
    }));

    setSeries(transformedData);
  }, [rawData]);

  const options = {
    chart: {
      type: "heatmap",
      toolbar: { show: false },
    },
    plotOptions: {
      heatmap: {
        distributed: true,
        enableShades: false,
        colorScale: {
          ranges: scoreToColor,
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => {
        const { seriesIndex, dataPointIndex, w } = opts;
        const customVal =
          w.globals.initialSeries[seriesIndex].data[dataPointIndex].customValues;
        return `R${customVal}`;
      },
      style: {
        colors: ["#FFFFFF"],
        fontWeight: "400",
        fontSize: "12px",
      },
    },
    tooltip: {
      y: {
        formatter: (val, opts) => {
          const { seriesIndex, dataPointIndex, w } = opts;
          const customVal =
            w.globals.initialSeries[seriesIndex].data[dataPointIndex].customValues;
          return `Rating: R${customVal}`;
        },
      },
    },
    xaxis: {
      position: "top",
      categories: xAxisCategories,
      title: {
        text: "Category",
        style: {
          fontWeight: 600,
          fontSize: "14px",
          color: "#333",
        },
      },
      labels: {
        rotate: -90, // default no rotation
        style: {
          fontWeight: 500,
          fontSize: "12px",
          colors: "#B4B4B4",
        },
      },
    },
    yaxis: {
      categories: yAxisVendors,
      title: {
        text: "Vendors",
        style: {
          fontWeight: 600,
          fontSize: "14px",
          color: "#333",
        },
      },
      labels: {
        style: {
          fontWeight: 500,
          fontSize: "12px",
          colors: "#B4B4B4",
        },
      },
    },
    legend: {
      show: false,
    },

    // ✅ Responsive Rules
    responsive: [
      {
        breakpoint: 768, // tablet
        options: {
          xaxis: {
            
            title: {
              show: false, 
              text: undefined,
              style: {
                fontWeight: 600,
                fontSize: "14px",
                color: "#333",
              },
            },
            labels: {
              
              offsetY: -10,              
              style: {
                fontWeight: 500,
                fontSize: "12px",
                colors: "#B4B4B4",
              },
            },
            axisTicks: { show: false },
            axisBorder: { show: false },
          },
          yaxis: {
            labels: {
              style: {
                fontSize: "10px",
              },
            },
            title: {
              style: {
                fontSize: "12px",
              },
            },
          },
        },
      }
    ],
  };


  return (
    <Box
      sx={{
        width: "100%",
        height: "450px",
        border: "1px solid #D8D8D8",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "white",
        p: 2,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          fontSize: "18px",
          color: theme?.customSettings?.themeColor || "#333",
        }}
      >
        Vendor Performance Heatmap
      </Typography>
      <Typography sx={{ mt: 1, color: "#5052588A", fontSize: "14px" }}>
        Ratings of vendors across different performance categories.
      </Typography>

      {isLoading ? (
        <Box
          sx={{
            height: "330px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} />
          <Typography sx={{ ml: 2 }}>Loading data...</Typography>
        </Box>
      ) : series.length === 0 ? (
        <Box
          sx={{
            height: "330px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#999",
            fontSize: "16px",
            fontWeight: 500,
          }}
        >
          No data available.
        </Box>
      ) : (
        <ReactApexChart
          options={options}
          series={series}
          type="heatmap"
          height="330"
          style={{ cursor: "pointer", marginTop: "10px" }}
        />
      )}
    </Box>
  );
};

export default VendorHeatmap;
